import { ChangeDetectorRef, Component, CUSTOM_ELEMENTS_SCHEMA, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { RecoveryCodePurpose, RecoveryCodesResponse, UserSettingsService } from '../../../../../../services/members/user-settings.service';
import { GrowlerService } from '../../../../../shared/growler/growler.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { finalize, take } from 'rxjs/operators';
import { User } from '../../../../../../models/entities/user';
import { RecoveryCodesDialogComponent } from '../../../../../v2-layout/shared/recovery-codes-dialog/recovery-codes-dialog.component';
import { SetupGoogleAuthenticatorComponent } from './setup-google-authenticator/setup-google-authenticator.component';
import { SetupSmsAuthenticationComponent } from './setup-sms-authentication/setup-sms-authentication.component';
import { Confirm2faCodeComponent, ConfirmTwoFactorInput, ConfirmTwoFactorOutput } from '../confirm-2fa-code/confirm-2fa-code.component';
import { JwtLegFiClaims } from '../../../../../../services/auth/jwt-legfi-claims.model';
import { LegFiJwtService } from '../../../../../../services/auth/legfi-jwt.service';
import { SuperUser2faGuard } from '../../../../../../common/auth/super-user-2fa.guard';
import { SharedModule } from '../../../../../shared/shared.module';
import { ComparisonValidatorsModule } from '../../../../../../directives/validators/comparison/comparison-validators.module';
import { StringValidatorsModule } from '../../../../../../directives/validators/string/string-validators.module';

@UntilDestroy()
@Component({
    selector: 'app-two-factor-auth',
    templateUrl: './two-factor-auth.component.html',
    // The kit spaces a ph-columns from whatever follows it; here that is only the dialogs, so the row kept a stray 16px gap.
    styles: [':host { display: block; } :host > ph-columns { margin-block-end: 0; }'],
    standalone: true,
    imports: [
        // Shared
        SharedModule,
        // Dependencies
        ComparisonValidatorsModule,
        StringValidatorsModule,
        SetupGoogleAuthenticatorComponent,
        SetupSmsAuthenticationComponent,
        Confirm2faCodeComponent,
        RecoveryCodesDialogComponent,
    ],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class TwoFactorAuthComponent implements OnInit
{
    @Input() highlight2fa = false;
    /**
     * Compact/embedded presentation: hides this widget's own page heading and intro
     * paragraph (the host surface — e.g. the 2FA gate dialog — supplies its own title
     * and copy) and tightens the card layout. Default false keeps the Settings → Security
     * tab unchanged.
     */
    @Input() compact = false;
    @Output() enrollmentChecked = new EventEmitter<boolean>();
    /**
     * Compact/embedded mode only: a method's "Setup" was tapped. The host (the 2FA gate
     * stepper) swaps to the inline setup step instead of this widget opening a nested dialog.
     * Non-compact (Settings) ignores this and opens its dialog as before.
     */
    @Output() setupRequested = new EventEmitter<'google' | 'sms'>();

    isSmsAuthEnabled = false;
    smsLast4 = '';
    isGoogleAuthEnabled = false;
    isLoading = true;
    isSetupGoogleOpen = false;
    isSetupSmsOpen = false;
    twoFactorOpen = false;
    twoFactorData: ConfirmTwoFactorInput = {type: null};
    recoveryCodesOpen = false;
    recoveryCodes: string[] = [];
    private _onTwoFactorResult: ((result: ConfirmTwoFactorOutput | null) => void) | null = null;

    /** In-flight guard for the regenerate call — the request sits between the re-auth dialog
     *  closing and the show-once dialog opening, which would otherwise be silent. */
    isRegenerating = false;
    /**
     * SD-8982: whether the user already holds an active set. Drives "Generate" vs "Regenerate"
     * wording and whether the card warns about replacing existing codes. Everyone who enrolled in
     * 2FA before the flag went global (SD-8944) is enrolled with no codes, so false is a real,
     * common state — not just a pre-load default.
     */
    hasRecoveryCodes = false;
    jwt: JwtLegFiClaims;

    /**
     * SD-8982: one label for all four states of the recovery-codes button, kept here rather than as a
     * nested ternary in the template so the spec can assert it directly.
     */
    /** Recovery codes back up an enrolled method, so they can only be generated once one exists. */
    get hasTwoFactorMethod(): boolean {
        return this.isGoogleAuthEnabled || this.isSmsAuthEnabled;
    }

    get recoveryCodesStatus(): string {
        if (!this.hasTwoFactorMethod) {
            return 'Needs a method';
        }

        return this.hasRecoveryCodes ? 'Generated' : 'None yet';
    }

    /** The badge colour for {@link recoveryCodesStatus}: green only when there is a usable set (F-6c1f). */
    get recoveryCodesBadgeStatus(): 'completed' | 'disabled' {
        return this.hasTwoFactorMethod && this.hasRecoveryCodes ? 'completed' : 'disabled';
    }

    get recoveryCodesDescription(): string {
        if (!this.hasTwoFactorMethod) {
            return 'Available once you set up the authenticator app or text messages.';
        }

        return this.hasRecoveryCodes
            ? 'Use one to sign in if you lose access to your phone. Regenerating replaces the whole set.'
            : 'Generate a set to sign in if you lose access to your phone. They\'re shown only once.';
    }

    get recoveryCodesButtonLabel(): string {
        if (this.isRegenerating) {
            return this.hasRecoveryCodes ? 'Regenerating…' : 'Generating…';
        }

        return this.hasRecoveryCodes ? 'Regenerate recovery codes' : 'Generate recovery codes';
    }

    constructor(
            private _userSettings: UserSettingsService,
            private _growler: GrowlerService,
            private _superUser2faGuard: SuperUser2faGuard,
            private _changeDetector: ChangeDetectorRef,
    ) {
    }

    ngOnInit(): void {
        this.jwt = LegFiJwtService.read();
        this.loadAuthenticatorSettings();
    }

    setupGoogleAuth() {
        // In the gate stepper, hand off to the inline setup step (no nested dialog).
        if (this.compact) {
            this.setupRequested.emit('google');
            return;
        }
        this.isSetupGoogleOpen = true;
    }

    onSetupGoogleClosed(result: {success: boolean} | null): void {
        this.isSetupGoogleOpen = false;
        if (result?.success) {
            this.loadAuthenticatorSettings();
        }
    }

    setupSmsAuth() {
        // In the gate stepper, hand off to the inline setup step (no nested dialog).
        if (this.compact) {
            this.setupRequested.emit('sms');
            return;
        }
        this.isSetupSmsOpen = true;
    }

    onSetupSmsClosed(result: {success: boolean} | null): void {
        this.isSetupSmsOpen = false;
        if (result?.success) {
            this.loadAuthenticatorSettings();
        }
    }

    disableSmsAuth() {
        // Annotated const rather than a cast, so excess-property checking applies.
        const data: ConfirmTwoFactorInput = {
            type: 'sms',
            canGoBack: false,
            hasRecoveryCodes: this.hasRecoveryCodes,
            // SD-11060: the server skips the replacement only if this is the last factor (F-b022).
            recoveryPurpose: RecoveryCodePurpose.DISABLE,
        };

        this.openTwoFactor(data, (result: ConfirmTwoFactorOutput) => {
            if (result?.success) {
                this._userSettings.removeSmsAuth(result.token)
                        .pipe(take(1))
                        .subscribe({
                            next: () => {
                                this.loadAuthenticatorSettings();
                                this._growler.success('Success', 'SMS Authentication has been removed from your account.');
                            },
                        });
            }
        });
    }

    disableGoogleAuth() {
        // Annotated const rather than a cast, so excess-property checking applies.
        const data: ConfirmTwoFactorInput = {
            type: 'google',
            canGoBack: false,
            hasRecoveryCodes: this.hasRecoveryCodes,
            // SD-11060: the server skips the replacement only if this is the last factor (F-b022).
            recoveryPurpose: RecoveryCodePurpose.DISABLE,
        };

        this.openTwoFactor(data, (result: ConfirmTwoFactorOutput) => {
            if (result?.success) {
                this._userSettings.removeGoogleAuth(result.token)
                        .pipe(take(1))
                        .subscribe({
                            next: () => {
                                this.loadAuthenticatorSettings();
                                this._growler.success('Success', 'The authenticator app has been removed from your account.');
                            },
                        });
            }
        });
    }

    /**
     * Issue the user's recovery codes (SD-8082). Requires a fresh 2FA re-authentication
     * (reusing the Confirm2faCodeComponent flow, like the disable actions), then shows the new
     * codes exactly once. Only available while at least one 2FA method is enrolled.
     *
     * SD-8982: this same path serves a user's FIRST set as well as a replacement — the endpoint
     * replaces whatever set exists, which is a no-op deletion when there is none. Re-auth is
     * deliberately kept for both so there is one code path and no new branch in a security flow.
     * Only the wording differs, keyed off hasRecoveryCodes.
     */
    regenerateRecoveryCodes() {
        const type: 'google' | 'sms' = this.isGoogleAuthEnabled ? 'google' : 'sms';
        // Captured before the request: the success copy must describe what the user just did, and
        // hasRecoveryCodes flips to true as soon as the call lands.
        const wasFirstSet = !this.hasRecoveryCodes;

        // Annotated const rather than a cast, so excess-property checking applies.
        const data: ConfirmTwoFactorInput = {
            type,
            canGoBack: false,
            hasRecoveryCodes: this.hasRecoveryCodes,
            // SD-11060: regenerating replaces the whole set, so no replacement code is minted.
            recoveryPurpose: RecoveryCodePurpose.REGENERATE,
        };

        this.openTwoFactor(data, (result: ConfirmTwoFactorOutput) => {
            if (result?.success) {
                this.isRegenerating = true;
                this._userSettings.regenerateRecoveryCodes(result.type, result.token)
                        .pipe(
                                untilDestroyed(this),
                                finalize(() => this.isRegenerating = false),
                        )
                        .subscribe({
                            next: (response: RecoveryCodesResponse) => {
                                if (!response.recoveryCodes?.length) {
                                    this._growler.error('Error', 'No recovery codes were generated. Please try again.');
                                    return;
                                }
                                // The card must switch to the regenerate wording immediately —
                                // the AC rules out making the user reload to see it.
                                this.hasRecoveryCodes = true;
                                this.recoveryCodes = response.recoveryCodes;
                                this.recoveryCodesOpen = true;
                                this._growler.success('Success', wasFirstSet
                                        ? 'Your recovery codes have been generated. Save them somewhere safe — they are shown only once.'
                                        : 'Your recovery codes have been regenerated. Your previous codes no longer work.');
                            },
                            error: () => {
                                this._growler.error('Error', wasFirstSet
                                        ? 'We could not generate your recovery codes. Please try again.'
                                        : 'We could not regenerate your recovery codes. Please try again.');
                            },
                        });
            }
        });
    }

    onTwoFactorClosed(result: ConfirmTwoFactorOutput | null): void {
        this.twoFactorOpen = false;
        const onResult = this._onTwoFactorResult;
        this._onTwoFactorResult = null;
        onResult?.(result);
    }

    onRecoveryCodesClosed(): void {
        this.recoveryCodesOpen = false;
        this.recoveryCodes = [];
    }

    private openTwoFactor(data: ConfirmTwoFactorInput, onResult: (result: ConfirmTwoFactorOutput | null) => void): void {
        this.twoFactorData = data;
        this._onTwoFactorResult = onResult;
        this.twoFactorOpen = true;
    }

    loadAuthenticatorSettings() {
        this.isLoading = true;
        // Your own record, not the member read: the super 2FA gate refuses that one to an un-enrolled
        // super, which left the gate dialog unable to ever see them enrol (SD-10091).
        this._userSettings.getOwnUser().pipe(
            untilDestroyed(this),
            // The 2FA gate dialog hosting this is OnPush. A successful read redraws it through the
            // enrollmentChecked handler; a failed one emits nothing, so without this the cards stayed on
            // their skeletons for good and the gate could not be used (SD-10091).
            finalize(() => {
                this.isLoading = false;
                this._changeDetector.markForCheck();
            }),
        ).subscribe({
            next: (user: User) => {
                if (user.isSmsAuthVerificationEnrolled) {
                    this.isSmsAuthEnabled = true;
                    this.smsLast4 = user.smsLast4;
                } else {
                    this.isSmsAuthEnabled = false;
                    this.smsLast4 = '';
                }
                this.isGoogleAuthEnabled = user.isGoogleAuthVerificationEnrolled;
                this.hasRecoveryCodes = user.hasActiveRecoveryCodes;

                const enrolled = this.isSmsAuthEnabled || this.isGoogleAuthEnabled;
                if (enrolled) {
                    this._superUser2faGuard.clearCache();
                }
                this.enrollmentChecked.emit(enrolled);
            },
            // Emits nothing, so a gate hosting this stays up rather than guessing an enrolment it couldn't read.
            error: () => this._growler.error('Error', 'We could not load your two-factor settings. Please refresh and try again.'),
        });
    }
}
